import { replaceHtmlBreaks } from '@insurancemenu/imenu-components-lib/dist/helpers';
import { OptionType, ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types';
import {
  Choice,
  InterestedProductLine,
  PlanDetails,
  RequiredSubInfoPresenter,
  SubscriberProductLineType,
} from 'types/electionService';

/**
 * Derives high-level product line type (SELECTED / WAIVED / NOT_SELECTED)
 * from product id (Choice.productId) or value (InterestedProductLine.value).
 */
export const getPLineProductType = (value: string): SubscriberProductLineType => {
  switch (value) {
    case 'null':
      return 'WAIVED';
    case '':
      return 'NOT_SELECTED';
    default:
      return 'SELECTED';
  }
};

/**
 * Returns the full product name.
 * Used in the non-editable card view when the select input is disabled
 * and the product name might be truncated.
 */
export const getProductDisplayName = (productId: string, productName: string): string => {
  switch (getPLineProductType(productId)) {
    case 'WAIVED':
      return 'Waived';
    case 'NOT_SELECTED':
      return '--Make election--';
    case 'SELECTED':
    default:
      return productName;
  }
};

/**
 * Returns the display name for the product line interest value.
 * Used in the interest selection product line card select input.
 */
export const getProductLineInterestValueDisplayName = (value: string): string => {
  switch (getPLineProductType(value)) {
    case 'WAIVED':
      return 'Not interested';
    case 'NOT_SELECTED':
      return '--Make election--';
    case 'SELECTED':
    default:
      return 'Interested';
  }
};

/**
 * Finds a subscriber choice entry by product line id.
 */
export const getChoiceByProductLineId = (choice: Choice[], productLineId: ProductLineId): Choice | undefined => {
  return choice.find((subChoice) => subChoice.productLineId === productLineId);
};

export const getInterestedProductLineByProductLineId = (
  interestedProductLines: InterestedProductLine[],
  productLineId: ProductLineId,
): InterestedProductLine | undefined => {
  return interestedProductLines.find((line) => line.productLineId === productLineId);
};

/**
 * Get the product line type of the dependent product line for a given product line.
 * Returns null if there is no dependency or the dependent product line has no choice.
 * @returns The product line type of the dependent product line, or null if no dependency exists
 */
export const getDependentOnProductLineType = (
  productLineId: ProductLineId,
  choice: Choice[],
  requiredSubInfo?: RequiredSubInfoPresenter,
): SubscriberProductLineType | null => {
  const dependentOnProductLineId = requiredSubInfo?.productLineRequiredInfo?.[productLineId]?.dependentOn;

  if (!dependentOnProductLineId) {
    return null;
  }

  const dependentOnChoice = getChoiceByProductLineId(choice, dependentOnProductLineId);
  if (!dependentOnChoice) {
    return null;
  }

  return getPLineProductType(dependentOnChoice.productId || '');
};

/**
 * Same as {@link getDependentOnProductLineType}, but resolves the dependent line from
 * {@link InterestedProductLine} interest values instead of {@link Choice} product ids.
 */
export const getDependentOnProductLineTypeFromInterest = (
  productLineId: ProductLineId,
  interestedProductLines: InterestedProductLine[],
  requiredSubInfo?: RequiredSubInfoPresenter,
): SubscriberProductLineType | null => {
  const dependentOnProductLineId = requiredSubInfo?.productLineRequiredInfo?.[productLineId]?.dependentOn;

  if (!dependentOnProductLineId) {
    return null;
  }

  const dependentLine = getInterestedProductLineByProductLineId(interestedProductLines, dependentOnProductLineId);
  if (!dependentLine) {
    return null;
  }

  return getPLineProductType(dependentLine.value);
};

/**
 * Builds select options for plans, including default and waive choices.
 */
export const convertPlansToOptions = (plans: PlanDetails[], employeeClass: string): OptionType[] => {
  const filteredPlanOptions: OptionType[] = plans
    .filter(({ eligibleClasses }) => eligibleClasses.includes(employeeClass) || !employeeClass)
    .map(({ id: planId, name }) => ({
      value: planId,
      label: replaceHtmlBreaks(name),
    }));

  return [
    { value: '', label: '--Make election--' },
    ...filteredPlanOptions,
    { value: 'null', label: 'Waive Coverage' },
  ];
};

export const INTEREST_SELECTION_OPTIONS: OptionType[] = [
  { value: '', label: '--Make election--' },
  { value: 'I', label: 'Interested' },
  { value: 'null', label: 'Not interested' },
];
